import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WholesaleFactoryCard from "./components/WholesaleFactoryCard";
import SampleOrderModal from "./components/SampleOrderModal";
import BulkOrderModal from "./components/BulkOrderModal";
import { mockWholesaleFactories, mockWholesaleRegions, mockContainerPricing } from "@/mocks/wholesale";
import SEO from "@/components/base/SEO";

type ModalState = { type: "sample" | "bulk"; factoryIdx: number; productIdx: number } | null;

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const wholesaleSchema = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Global Wholesale Factory Directory — StarBookPrime",
    "description": "Buy direct from verified factories in USA, Canada, Europe, and Asia. Container orders, bulk wholesale, and sample-first purchasing. MOQ from $8,000.",
    "url": `${SITE_URL}/wholesale`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Wholesale", "item": `${SITE_URL}/wholesale` }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Global Wholesale Factory Sourcing",
    "description": "Buy direct from verified factories in USA, Canada, Europe, and Asia. Container orders, bulk wholesale, and sample-first purchasing. MOQ from $8,000. 120+ countries served.",
    "provider": {
      "@type": "Organization",
      "name": "StarBookPrime",
      "url": SITE_URL,
    },
    "serviceType": "Wholesale B2B Sourcing",
    "areaServed": "Worldwide",
    "offers": {
      "@type": "Offer",
      "description": "Minimum order value $8,000 USD. Container orders: 20ft, 40ft, 40ft HC. Sample orders available.",
      "priceCurrency": "USD",
      "price": "8000",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "minPrice": "8000",
        "priceCurrency": "USD",
        "description": "Minimum order value per factory per order",
      },
    },
  },
];

export default function WholesalePage() {
  const [activeRegion, setActiveRegion] = useState("all");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [modal, setModal] = useState<ModalState>(null);

  const categories = ["all", "Electronics", "Fashion", "Furniture", "Packaging", "Beauty", "Food"];

  const filtered = mockWholesaleFactories.filter((f) => {
    const matchRegion = activeRegion === "all" || f.region === activeRegion;
    const matchCat = activeCategory === "all" || f.category.toLowerCase().includes(activeCategory.toLowerCase());
    const matchSearch = !searchQuery || f.name.toLowerCase().includes(searchQuery.toLowerCase()) || f.category.toLowerCase().includes(searchQuery.toLowerCase()) || f.country.toLowerCase().includes(searchQuery.toLowerCase());
    return matchRegion && matchCat && matchSearch;
  });

  const activeFactory = modal !== null ? mockWholesaleFactories[modal.factoryIdx] : null;
  const activeProduct = modal !== null && activeFactory ? activeFactory.products[modal.productIdx] : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f5f5" }}>
      <SEO
        title="Global Wholesale Factories — USA, Canada, Europe, Asia | StarBookPrime"
        description="Buy direct from verified factories worldwide. Container orders, bulk wholesale, and sample-first purchasing. MOQ from $8,000. 120+ countries served."
        keywords="global wholesale factory, bulk order factory, container order wholesale"
        canonical="/wholesale"
        ogImage="https://public.readdy.ai/ai/img_res/590799262cdb3e167123a25d554da291.jpg"
        schema={wholesaleSchema}
      />
      <Navbar />

      {/* Modals */}
      {modal?.type === "sample" && activeFactory && activeProduct && (
        <SampleOrderModal
          factory={activeFactory}
          product={activeProduct}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "bulk" && activeFactory && activeProduct && (
        <BulkOrderModal
          factory={activeFactory}
          product={activeProduct}
          onClose={() => setModal(null)}
        />
      )}

      <div className="pt-16">
        {/* Hero Banner */}
        <div className="relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=international%20shipping%20port%20cargo%20containers%20aerial%20view%20global%20trade%20logistics%20colorful%20containers%20ocean%20ships%20sunset%20dramatic%20wide%20shot&width=1440&height=480&seq=wholehero&orientation=landscape"
            alt="Global Wholesale"
            className="w-full h-72 md:h-96 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20 flex items-center">
            <div className="max-w-6xl mx-auto px-6 md:px-12 w-full">
              <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4">Global B2B Wholesale</span>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-3">
                Buy Direct from<br />
                <span className="text-orange-400">Verified Factories</span> Worldwide
              </h1>
              <p className="text-white/80 text-base md:text-lg mb-6 max-w-xl">
                USA · Canada · Europe · Asia — Container orders, bulk wholesale, and sample-first purchasing. MOQ from $8,000.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: "ri-flask-line", label: "Order Sample First", color: "bg-green-500" },
                  { icon: "ri-ship-line", label: "Full Container Orders", color: "bg-orange-500" },
                  { icon: "ri-shield-check-line", label: "Verified Factories Only", color: "bg-gray-700" },
                ].map((b) => (
                  <span key={b.label} className={`flex items-center gap-2 ${b.color} text-white text-sm font-semibold px-4 py-2 rounded-full`}>
                    <i className={b.icon} />{b.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { value: "6 Regions", label: "USA, Canada, Europe, Asia & more", icon: "ri-global-line", color: "text-orange-500 bg-orange-50" },
              { value: "$8,000+", label: "Minimum order value", icon: "ri-money-dollar-circle-line", color: "text-green-600 bg-green-50" },
              { value: "Sample First", label: "Test before bulk order", icon: "ri-flask-line", color: "text-green-500 bg-green-50" },
              { value: "20ft–40ft HC", label: "Container types available", icon: "ri-truck-line", color: "text-gray-600 bg-gray-100" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-5">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${s.color}`}>
                  <i className={`${s.icon} text-lg`} />
                </div>
                <div className="text-lg font-extrabold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 mb-10">
            <h2 className="text-xl font-extrabold text-gray-900 mb-6 text-center">How Wholesale Ordering Works</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { step: "1", icon: "ri-search-line", title: "Browse Factories", desc: "Filter by region, category, and certifications. All factories are verified.", color: "bg-orange-50 text-orange-500" },
                { step: "2", icon: "ri-flask-line", title: "Order a Sample", desc: "Request product samples first. Sample cost is deducted from your bulk order.", color: "bg-green-50 text-green-500" },
                { step: "3", icon: "ri-file-list-3-line", title: "Place Bulk Order", desc: "Minimum $8,000 per order. Choose units or full container (20ft / 40ft / 40ft HC).", color: "bg-orange-50 text-orange-500" },
                { step: "4", icon: "ri-ship-line", title: "Factory Ships", desc: "Factory ships from local or overseas warehouse. Track your container in real time.", color: "bg-gray-50 text-gray-600" },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-3 relative ${s.color}`}>
                    <i className={`${s.icon} text-2xl`} />
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-orange-500 text-white text-xs font-bold rounded-full">{s.step}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{s.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Container Guide */}
          <div className="mb-10">
            <h2 className="text-xl font-extrabold text-gray-900 mb-4">Container Size Guide</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {mockContainerPricing.map((c) => (
                <div key={c.type} className="bg-white rounded-xl border border-gray-100 p-5 flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-xl flex-shrink-0">
                    <i className="ri-truck-line text-orange-500 text-2xl" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{c.label}</p>
                    <p className="text-xs text-orange-600 font-semibold mt-0.5">{c.capacity}</p>
                    <p className="text-xs text-gray-500 mt-1">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search factories, categories, countries..."
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors"
                />
              </div>
              {/* Region Filter */}
              <div className="flex flex-wrap gap-2">
                {mockWholesaleRegions.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setActiveRegion(r.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeRegion === r.id ? "bg-orange-500 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
                  >
                    {"flag" in r && <span>{r.flag}</span>}
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mt-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeCategory === cat ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  {cat === "all" ? "All Categories" : cat}
                </button>
              ))}
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-gray-500">
              <strong className="text-gray-900">{filtered.length}</strong> verified factories found
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <i className="ri-shield-check-fill text-green-500" /> All factories verified
            </div>
          </div>

          {/* Factory Grid */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-50 rounded-full">
                <i className="ri-building-2-line text-3xl text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">No factories found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((factory, fi) => (
                <WholesaleFactoryCard
                  key={factory.id}
                  factory={factory}
                  onSampleOrder={(f, p) => {
                    const pi = f.products.indexOf(p);
                    setModal({ type: "sample", factoryIdx: fi, productIdx: pi >= 0 ? pi : 0 });
                  }}
                  onBulkOrder={(f, p) => {
                    const pi = f.products.indexOf(p);
                    setModal({ type: "bulk", factoryIdx: fi, productIdx: pi >= 0 ? pi : 0 });
                  }}
                />
              ))}
            </div>
          )}

          {/* MOQ Info Banner */}
          <div className="mt-12 bg-gray-900 rounded-2xl p-8 md:p-12 text-white">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <h2 className="text-2xl font-extrabold mb-3">Minimum Order Requirements</h2>
                <div className="space-y-3">
                  {[
                    { icon: "ri-money-dollar-circle-line", text: "Minimum order value: $8,000 USD per factory per order" },
                    { icon: "ri-ship-line", text: "Container orders: 1 container minimum (20ft, 40ft, or 40ft HC)" },
                    { icon: "ri-flask-line", text: "Sample orders: Available from most factories — cost deducted from first bulk order" },
                    { icon: "ri-file-list-3-line", text: "Payment: Proforma Invoice → Wire Transfer, L/C, or PayPal (under $50K)" },
                    { icon: "ri-edit-line", text: "Custom branding & private label available from most verified factories" },
                  ].map((item) => (
                    <div key={item.text} className="flex items-start gap-3 text-sm text-gray-300">
                      <i className={`${item.icon} text-orange-400 mt-0.5 flex-shrink-0`} />
                      {item.text}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 text-center">
                <div className="w-24 h-24 flex items-center justify-center bg-orange-500 rounded-2xl mx-auto mb-3">
                  <i className="ri-global-line text-white text-4xl" />
                </div>
                <p className="text-orange-400 font-bold text-lg">120+ Countries</p>
                <p className="text-gray-400 text-sm">Shipping worldwide</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
